import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Trash2, Upload, CheckCircle, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Validation helpers
const isValidEmail = (email) => {
  if (!email) return false;
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  return emailRegex.test(email.trim());
};

const isValidPhone = (phone) => {
  if (!phone) return false;
  const cleaned = phone.replace(/[^\d+]/g, '');
  const phoneRegex = /^\+?\d{10,15}$/;
  return phoneRegex.test(cleaned);
};

const formatPhoneInput = (value) => {
  let cleaned = value.replace(/[^\d+]/g, '');
  if (cleaned.indexOf('+') > 0) {
    cleaned = cleaned.replace(/\+/g, '');
  }
  if (cleaned.startsWith('+')) {
    cleaned = '+' + cleaned.slice(1).replace(/\+/g, '');
  }
  return cleaned;
};

const isValidUrl = (url) => {
  if (!url) return false;
  try {
    const urlObj = new URL(url);
    return urlObj.protocol === 'http:' || urlObj.protocol === 'https:';
  } catch {
    return false;
  }
};

const isValidLinkedIn = (url) => {
  if (!url || !isValidUrl(url)) return false;
  const urlLower = url.toLowerCase();
  return urlLower.includes('linkedin.com/in/') || urlLower.includes('linkedin.com/company/');
};

const formatNumberInput = (value) => {
  return value.replace(/[^\d]/g, '');
};

// Helper to construct correct file URL
const getFileUrl = (fileUrl) => {
  if (!fileUrl) return null;
  if (fileUrl.startsWith('https://') && (fileUrl.includes('.s3.') || fileUrl.includes('amazonaws.com'))) {
    return fileUrl;
  }
  let filePath = fileUrl;
  if (filePath.startsWith('http')) {
    try {
      const url = new URL(filePath);
      filePath = url.pathname;
    } catch (e) {
      filePath = '/' + filePath.split('/').slice(3).join('/');
    }
  }
  if (filePath.startsWith('/uploads')) {
    filePath = filePath.replace('/uploads', '/api/uploads');
  } else if (!filePath.startsWith('/api/uploads')) {
    filePath = '/api/uploads/' + filePath.split('/').pop();
  }
  return `${process.env.REACT_APP_BACKEND_URL}${filePath}`;
};

export default function StartupFormPage() {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [uploadingFields, setUploadingFields] = useState({});
  const [errors, setErrors] = useState({});
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Form state
  const [formData, setFormData] = useState({
    legal_name: '',
    brand_name: '',
    website: '',
    location: '',
    founders: [{ name: '', email: '', phone: '', linkedin: '' }],
    stage: '',
    category: '',
    one_liner: '',
    description: '',
    product: '',
    product_pricing: '',
    previous_fundraises: [],
    deck_url: '',
    demo_link: '',
    amount_to_raise: '',
    terms_type: '',
    valuation_amount: '',
    safe_terms: '',
    other_terms: ''
  });

  // Fetch form configuration from backend
  const fetchFormConfig = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/form-configurations/startup/public`);
      setFields(response.data.fields || []);
    } catch (error) {
      console.error('Error fetching form config:', error);
      // Form will still work with default fields
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchFormConfig();
  }, [fetchFormConfig]);

  const handleInputChange = (fieldKey, value) => {
    setFormData({ ...formData, [fieldKey]: value });
    if (errors[fieldKey]) {
      setErrors({ ...errors, [fieldKey]: null });
    }
  };

  const handleFileUpload = async (fieldKey, file) => {
    if (!file) return;

    const allowedTypes = ['application/pdf', 'application/vnd.ms-powerpoint', 'application/vnd.openxmlformats-officedocument.presentationml.presentation'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Only PDF and PPT files are allowed');
      return;
    }

    setUploadingFields({ ...uploadingFields, [fieldKey]: true });
    
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      
      const response = await axios.post(`${API}/startup-applications/upload-deck`, formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      handleInputChange(fieldKey, response.data.file_url);
      toast.success('File uploaded successfully');
    } catch (error) {
      console.error('File upload error:', error);
      toast.error('Failed to upload file');
    } finally {
      setUploadingFields({ ...uploadingFields, [fieldKey]: false });
    }
  };

  const handleRemoveFile = (fieldKey) => {
    handleInputChange(fieldKey, '');
  };

  // Founders handlers
  const handleFounderChange = (index, field, value) => {
    let processedValue = value;
    if (field === 'phone') {
      processedValue = formatPhoneInput(value);
    }
    const founders = [...formData.founders];
    founders[index] = { ...founders[index], [field]: processedValue };
    setFormData({ ...formData, founders });
    
    // Clear founder-specific errors
    const errorKey = `founder_${index}_${field}`;
    if (errors[errorKey]) {
      setErrors({ ...errors, [errorKey]: null });
    }
  };

  const addFounder = () => {
    setFormData({
      ...formData,
      founders: [...formData.founders, { name: '', email: '', phone: '', linkedin: '' }]
    });
  };

  const removeFounder = (index) => {
    if (formData.founders.length > 1) {
      setFormData({
        ...formData,
        founders: formData.founders.filter((_, i) => i !== index)
      });
    }
  };

  // Previous fundraises handlers
  const handleFundraiseChange = (index, field, value) => {
    let processedValue = value;
    if (field === 'valuation' || field === 'amount') {
      processedValue = formatNumberInput(value);
    }
    const fundraises = [...formData.previous_fundraises];
    fundraises[index] = { ...fundraises[index], [field]: processedValue };
    
    if (field === 'type') {
      if (value === 'Fundraise') {
        fundraises[index].grant_name = '';
      } else if (value === 'Grant') {
        fundraises[index].valuation = '';
      }
    }
    
    setFormData({ ...formData, previous_fundraises: fundraises });
  };

  const addFundraise = () => {
    setFormData({
      ...formData,
      previous_fundraises: [...formData.previous_fundraises, { type: 'Fundraise', valuation: '', grant_name: '', amount: '' }]
    });
  };

  const removeFundraise = (index) => {
    setFormData({
      ...formData,
      previous_fundraises: formData.previous_fundraises.filter((_, i) => i !== index)
    });
  };

  // Validation
  const validateForm = () => {
    const newErrors = {};
    
    // Validate based on dynamic fields configuration
    fields.forEach((field) => {
      if (!field.required) return;
      
      const value = formData[field.field_key];
      
      // Skip special compound types - validate separately
      if (field.type === 'founders') {
        if (!formData.founders || formData.founders.length === 0 || !formData.founders[0]?.name?.trim()) {
          newErrors.founders = 'At least one founder with name is required';
        } else {
          formData.founders.forEach((founder, idx) => {
            if (founder.name && !founder.name.trim()) {
              newErrors[`founder_${idx}_name`] = 'Founder name is required';
            }
            if (founder.email && !isValidEmail(founder.email)) {
              newErrors[`founder_${idx}_email`] = 'Invalid email format';
            }
            if (founder.phone && !isValidPhone(founder.phone)) {
              newErrors[`founder_${idx}_phone`] = 'Invalid phone format';
            }
            if (founder.linkedin && !isValidLinkedIn(founder.linkedin)) {
              newErrors[`founder_${idx}_linkedin`] = 'Must be a valid LinkedIn URL';
            }
          });
        }
        return;
      }
      
      if (field.type === 'previous_fundraises') return; // Optional
      
      // Check required fields
      if (!value || (typeof value === 'string' && !value.trim())) {
        newErrors[field.field_key] = `${field.label} is required`;
        return;
      }
      
      // Type-specific validation
      switch (field.type) {
        case 'email':
          if (!isValidEmail(value)) newErrors[field.field_key] = 'Invalid email format';
          break;
        case 'phone':
          if (!isValidPhone(value)) newErrors[field.field_key] = 'Invalid phone number';
          break;
        case 'url':
          if (!isValidUrl(value)) newErrors[field.field_key] = 'Invalid URL (must start with http:// or https://)';
          break;
        case 'number':
          if (isNaN(Number(value))) newErrors[field.field_key] = 'Must be a valid number';
          break;
      }
    });
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error('Please fix the validation errors');
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        ...formData,
        amount_to_raise: parseInt(formData.amount_to_raise) || 0,
        valuation_amount: formData.valuation_amount ? parseInt(formData.valuation_amount) : null,
      };

      await axios.post(`${API}/startup-applications`, payload);
      setSubmitted(true);
      toast.success('Application submitted successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit application');
    } finally {
      setSubmitting(false);
    }
  };

  // Render a single field based on its type
  const renderField = (field) => {
    const value = formData[field.field_key] || '';
    const error = errors[field.field_key];
    const isUploading = uploadingFields[field.field_key];

    switch (field.type) {
      case 'text':
        return (
          <div key={field.id} className="space-y-2">
            <Label className="text-[#0F172A]">
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </Label>
            <Input
              value={value}
              onChange={(e) => handleInputChange(field.field_key, e.target.value)}
              placeholder={field.placeholder}
              className={`border-[#E2E8F0] ${error ? 'border-red-500' : ''}`}
              maxLength={field.validation?.maxLength}
            />
            {field.validation?.maxLength && (
              <p className="text-xs text-gray-500">{value.length}/{field.validation.maxLength}</p>
            )}
            {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
          </div>
        );

      case 'textarea':
        return (
          <div key={field.id} className="space-y-2">
            <Label className="text-[#0F172A]">
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </Label>
            <Textarea
              value={value}
              onChange={(e) => handleInputChange(field.field_key, e.target.value)}
              placeholder={field.placeholder}
              className={`border-[#E2E8F0] min-h-[100px] ${error ? 'border-red-500' : ''}`}
            />
            {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
          </div>
        );

      case 'email':
        return (
          <div key={field.id} className="space-y-2">
            <Label className="text-[#0F172A]">
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </Label>
            <Input
              type="email"
              value={value}
              onChange={(e) => handleInputChange(field.field_key, e.target.value)}
              placeholder={field.placeholder || 'email@example.com'}
              className={`border-[#E2E8F0] ${error ? 'border-red-500' : ''}`}
            />
            {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
          </div>
        );

      case 'phone':
        return (
          <div key={field.id} className="space-y-2">
            <Label className="text-[#0F172A]">
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </Label>
            <Input
              type="tel"
              value={value}
              onChange={(e) => handleInputChange(field.field_key, formatPhoneInput(e.target.value))}
              placeholder={field.placeholder || '+91 9876543210'}
              className={`border-[#E2E8F0] ${error ? 'border-red-500' : ''}`}
            />
            {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
          </div>
        );

      case 'url':
        return (
          <div key={field.id} className="space-y-2">
            <Label className="text-[#0F172A]">
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </Label>
            <Input
              type="url"
              value={value}
              onChange={(e) => handleInputChange(field.field_key, e.target.value)}
              placeholder={field.placeholder || 'https://example.com'}
              className={`border-[#E2E8F0] ${error ? 'border-red-500' : ''}`}
            />
            {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
          </div>
        );

      case 'number':
        return (
          <div key={field.id} className="space-y-2">
            <Label className="text-[#0F172A]">
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </Label>
            <Input
              type="text"
              value={value}
              onChange={(e) => handleInputChange(field.field_key, formatNumberInput(e.target.value))}
              placeholder={field.placeholder || 'Enter number'}
              className={`border-[#E2E8F0] ${error ? 'border-red-500' : ''}`}
            />
            {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
          </div>
        );

      case 'select':
        return (
          <div key={field.id} className="space-y-2">
            <Label className="text-[#0F172A]">
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </Label>
            <Select value={value} onValueChange={(val) => handleInputChange(field.field_key, val)}>
              <SelectTrigger className={`border-[#E2E8F0] ${error ? 'border-red-500' : ''}`}>
                <SelectValue placeholder={field.placeholder || 'Select option'} />
              </SelectTrigger>
              <SelectContent className="bg-white">
                {(field.options || []).map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
          </div>
        );

      case 'date':
        return (
          <div key={field.id} className="space-y-2">
            <Label className="text-[#0F172A]">
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </Label>
            <Input
              type="date"
              value={value}
              onChange={(e) => handleInputChange(field.field_key, e.target.value)}
              className={`border-[#E2E8F0] ${error ? 'border-red-500' : ''}`}
            />
            {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
          </div>
        );

      case 'file':
        return (
          <div key={field.id} className="space-y-2">
            <Label className="text-[#0F172A]">
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </Label>
            <div className={`relative border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-all
              ${isUploading ? 'border-gray-400 bg-gray-50' : 'border-[#E2E8F0] hover:border-brand'}
              ${value ? 'border-green-500 bg-green-50' : ''}
              ${error ? 'border-red-500' : ''}
            `}>
              {isUploading ? (
                <div className="flex items-center justify-center gap-2">
                  <Loader2 className="h-6 w-6 animate-spin text-brand" />
                  <span className="text-gray-500">Uploading...</span>
                </div>
              ) : value ? (
                <div className="space-y-2">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                    <CheckCircle className="h-6 w-6 text-green-600" />
                  </div>
                  <p className="text-green-600 font-medium">File uploaded successfully!</p>
                  <div className="flex items-center justify-center gap-3 relative z-10">
                    <a href={getFileUrl(value)} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:underline">
                      View file →
                    </a>
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); handleRemoveFile(field.field_key); }}
                      className="text-sm text-red-500 hover:text-red-700 hover:underline"
                    >
                      Remove
                    </button>
                  </div>
                  <p className="text-xs text-gray-500">Click to replace</p>
                </div>
              ) : (
                <div>
                  <Upload className="mx-auto h-10 w-10 text-gray-400 mb-2" />
                  <p className="text-gray-600">{field.placeholder || 'Click to upload your pitch deck'}</p>
                  <p className="text-xs text-gray-400 mt-1">PDF or PPT format</p>
                </div>
              )}
              <input
                type="file"
                accept=".pdf,.ppt,.pptx"
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                onChange={(e) => handleFileUpload(field.field_key, e.target.files[0])}
                disabled={isUploading}
              />
            </div>
            {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
          </div>
        );

      case 'founders':
        return (
          <div key={field.id} className="space-y-4">
            <div className="flex items-center justify-between">
              <Label className="text-[#0F172A] font-semibold">
                {field.label}
                {field.required && <span className="text-red-500 ml-1">*</span>}
              </Label>
              <Button type="button" variant="outline" size="sm" onClick={addFounder} className="border-brand text-brand">
                <Plus className="h-4 w-4 mr-1" /> Add Co-founder
              </Button>
            </div>
            {errors.founders && <p className="text-red-500 text-xs">{errors.founders}</p>}
            {formData.founders.map((founder, index) => (
              <div key={index} className="p-4 bg-gray-50 rounded-lg border border-[#E2E8F0]">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-medium text-gray-700">
                    {index === 0 ? 'Founder' : `Co-founder ${index}`}
                  </span>
                  {index > 0 && (
                    <Button type="button" variant="ghost" size="sm" onClick={() => removeFounder(index)} className="text-red-500 h-8 w-8 p-0">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs text-gray-600">Name *</Label>
                    <Input
                      value={founder.name}
                      onChange={(e) => handleFounderChange(index, 'name', e.target.value)}
                      placeholder="Full name"
                      className={`border-[#E2E8F0] ${errors[`founder_${index}_name`] ? 'border-red-500' : ''}`}
                    />
                    {errors[`founder_${index}_name`] && <p className="text-red-500 text-xs">{errors[`founder_${index}_name`]}</p>}
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-gray-600">Email *</Label>
                    <Input
                      type="email"
                      value={founder.email}
                      onChange={(e) => handleFounderChange(index, 'email', e.target.value)}
                      placeholder="email@example.com"
                      className={`border-[#E2E8F0] ${errors[`founder_${index}_email`] ? 'border-red-500' : ''}`}
                    />
                    {errors[`founder_${index}_email`] && <p className="text-red-500 text-xs">{errors[`founder_${index}_email`]}</p>}
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-gray-600">Phone *</Label>
                    <Input
                      type="tel"
                      value={founder.phone}
                      onChange={(e) => handleFounderChange(index, 'phone', e.target.value)}
                      placeholder="+91 9876543210"
                      className={`border-[#E2E8F0] ${errors[`founder_${index}_phone`] ? 'border-red-500' : ''}`}
                    />
                    {errors[`founder_${index}_phone`] && <p className="text-red-500 text-xs">{errors[`founder_${index}_phone`]}</p>}
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-gray-600">LinkedIn URL</Label>
                    <Input
                      value={founder.linkedin}
                      onChange={(e) => handleFounderChange(index, 'linkedin', e.target.value)}
                      placeholder="https://linkedin.com/in/..."
                      className={`border-[#E2E8F0] ${errors[`founder_${index}_linkedin`] ? 'border-red-500' : ''}`}
                    />
                    {errors[`founder_${index}_linkedin`] && <p className="text-red-500 text-xs">{errors[`founder_${index}_linkedin`]}</p>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        );

      case 'previous_fundraises':
        return (
          <div key={field.id} className="space-y-4">
            <div className="flex items-center justify-between">
              <Label className="text-[#0F172A] font-semibold">
                {field.label}
                <span className="text-gray-500 font-normal ml-1">(Optional)</span>
              </Label>
              <Button type="button" variant="outline" size="sm" onClick={addFundraise} className="border-brand text-brand">
                <Plus className="h-4 w-4 mr-1" /> Add
              </Button>
            </div>
            {formData.previous_fundraises.length === 0 ? (
              <p className="text-sm text-gray-500 italic">No previous fundraises added. Click &ldquo;Add&rdquo; to add one.</p>
            ) : (
              formData.previous_fundraises.map((fundraise, index) => (
                <div key={index} className="p-4 bg-gray-50 rounded-lg border border-[#E2E8F0]">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm font-medium text-gray-700">Fundraise {index + 1}</span>
                    <Button type="button" variant="ghost" size="sm" onClick={() => removeFundraise(index)} className="text-red-500 h-8 w-8 p-0">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs text-gray-600">Type</Label>
                      <Select value={fundraise.type || 'Fundraise'} onValueChange={(val) => handleFundraiseChange(index, 'type', val)}>
                        <SelectTrigger className="border-[#E2E8F0]">
                          <SelectValue placeholder="Select type" />
                        </SelectTrigger>
                        <SelectContent className="bg-white">
                          <SelectItem value="Fundraise">Fundraise</SelectItem>
                          <SelectItem value="Grant">Grant</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    {fundraise.type === 'Fundraise' || !fundraise.type ? (
                      <div className="space-y-1">
                        <Label className="text-xs text-gray-600">Valuation (INR)</Label>
                        <Input
                          value={fundraise.valuation || ''}
                          onChange={(e) => handleFundraiseChange(index, 'valuation', e.target.value)}
                          placeholder="Enter valuation"
                          className="border-[#E2E8F0]"
                        />
                      </div>
                    ) : (
                      <div className="space-y-1">
                        <Label className="text-xs text-gray-600">Grant Name</Label>
                        <Input
                          value={fundraise.grant_name || ''}
                          onChange={(e) => handleFundraiseChange(index, 'grant_name', e.target.value)}
                          placeholder="Enter grant name"
                          className="border-[#E2E8F0]"
                        />
                      </div>
                    )}
                    <div className="space-y-1 md:col-span-2">
                      <Label className="text-xs text-gray-600">Amount Raised (INR)</Label>
                      <Input
                        value={fundraise.amount || ''}
                        onChange={(e) => handleFundraiseChange(index, 'amount', e.target.value)}
                        placeholder="Enter amount raised"
                        className="border-[#E2E8F0]"
                      />
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        );

      default:
        return (
          <div key={field.id} className="space-y-2">
            <Label className="text-[#0F172A]">{field.label}</Label>
            <Input
              value={value}
              onChange={(e) => handleInputChange(field.field_key, e.target.value)}
              placeholder={field.placeholder}
              className="border-[#E2E8F0]"
            />
          </div>
        );
    }
  };

  // Group fields by section
  const groupedFields = fields.reduce((acc, field) => {
    const section = field.section || 'General';
    if (!acc[section]) acc[section] = [];
    acc[section].push(field);
    return acc;
  }, {});

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#F8F9FA] flex items-center justify-center p-4">
        <Card className="max-w-md w-full border-[#E2E8F0] shadow-lg">
          <CardContent className="pt-12 pb-8 text-center">
            <div className="w-20 h-20 bg-[#ECFDF5] rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="h-10 w-10 text-[#10B981]" />
            </div>
            <h2 className="text-2xl font-bold text-[#0F172A] mb-3">Application Submitted!</h2>
            <p className="text-[#64748B] mb-6">
              Thank you for submitting your startup application. Our team will review it and get back to you soon.
            </p>
            <Button 
              onClick={() => navigate('/')}
              className="!bg-brand hover:!bg-brand-hover !text-white"
            >
              Back to Home
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8F9FA] py-8 px-4">
      <div className="max-w-3xl mx-auto">
        <Card className="border-[#E2E8F0] shadow-lg">
          <CardHeader className="border-b border-[#E2E8F0] bg-brand text-white rounded-t-lg">
            <CardTitle className="text-2xl font-bold">Startup Application Form</CardTitle>
            <CardDescription className="text-blue-100">
              Submit your startup for investment consideration
            </CardDescription>
          </CardHeader>
          <CardContent className="p-6">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-brand" />
                <span className="ml-2 text-gray-500">Loading form...</span>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-8">
                {Object.entries(groupedFields).map(([section, sectionFields]) => (
                  <div key={section} className="space-y-4">
                    <h3 className="text-lg font-semibold text-[#0F172A] border-b border-[#E2E8F0] pb-2">
                      {section}
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {sectionFields
                        .sort((a, b) => a.order - b.order)
                        .map((field) => {
                          // Full width for certain field types
                          const isFullWidth = ['textarea', 'founders', 'previous_fundraises', 'file'].includes(field.type);
                          return (
                            <div key={field.id} className={isFullWidth ? 'md:col-span-2' : ''}>
                              {renderField(field)}
                            </div>
                          );
                        })}
                    </div>
                  </div>
                ))}

                <div className="pt-4 border-t border-[#E2E8F0]">
                  <Button
                    type="submit"
                    disabled={submitting}
                    className="w-full h-12 text-lg font-semibold !bg-[#10B981] hover:!bg-[#059669] !text-white disabled:!bg-[#94A3B8]"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="h-5 w-5 animate-spin mr-2" />
                        Submitting...
                      </>
                    ) : (
                      'Submit Application'
                    )}
                  </Button>
                </div>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
